// WoEditor (R115 maint — Jesse A2: "clicking a WO on the calendar opens a popup
// with EVERYTHING you can do on the Maintenance tab"). The work-order edit
// surface shared by the Maintenance board card (MaintenancePage.renderWoCard)
// and the calendar hub's WO inspector (client/src/pages/calendar/maint): the
// four labeled fieldsets of R101 #8 — Who & what · When · Money & billing ·
// Guest access — with EXACTLY the data attributes the board has always used
// (data-mn-assign, -worole, -wovendor, -woprio, -wostatus, -wosched, -wodue,
// -woduesrc, -woasset, -wocost, -wopayer, -wobilled, -woskill, -woclaim,
// -wogp, -wogpmode, -wobillhint), so the board's own handlers keep working,
// plus ONE reader (readWoForm) and ONE diff (lib/woEdit.mjs diffWoEdit) so the
// two surfaces can never send different POST bodies for the same edit.
//
// Money (R115 DECISIONS): ctx.money=false renders no Money & billing inputs —
// the calendar passes canSeeWoMoney(role) (admin / director / accounting); the
// board keeps money for every staff role this round (flagged for Jesse). The
// claim-tracked switch is not money and stays on screen either way.
import { Formatters } from '../lib/formatters';
import { infoIcon } from './InfoIcon';
import { WO_BILLED_OPTIONS, woBillHintHtml, schedOf, priorityOf } from '../lib/woFormat';
import { diffWoEdit, type WoEditForm } from '../../../../lib/woEdit.mjs';

export const WO_STATUS_OPTS: Array<[string, string]> = [['open', 'Open'], ['in_progress', 'In progress'], ['done', 'Done']];
export const WO_PRIORITY_OPTS: Array<[string, string]> = [['emergency', 'Emergency'], ['urgent', 'Urgent'], ['routine', 'Routine'], ['pm', 'PM']];
export const WO_ROLE_OPTS: Array<[string, string]> = [['maintenance', 'Maintenance'], ['cleaner', 'Cleaner'], ['inspector', 'Inspector']];
export const WO_PAYER_OPTS: Array<[string, string]> = [['wch', 'WCH'], ['owner', 'Owner']];

export interface WoEditorVendor { id: string; name: string; archived?: boolean }
export interface WoEditorAsset { id: string; name: string; listing_id?: string | number | null }
export interface WoEditorCtx {
  /** Show the Money & billing inputs (cost / who pays / billed yet / skill). */
  money: boolean;
  vendors: WoEditorVendor[];
  /** Assets of THIS work order's listing (the caller filters). */
  assets: WoEditorAsset[];
  /** id of the assignee <datalist> already on the page (board: 'mn-assign-roster'). */
  rosterId: string;
}

const esc = (s: unknown): string => Formatters.escapeHtml(s == null ? '' : String(s));
const sel = (cond: boolean) => (cond ? 'selected' : '');

/** <option>s for a vendor select (archived vendors hidden unless already chosen). */
export function vendorOptionsHtml(vendors: WoEditorVendor[], selectedId: string | null): string {
  const opts = (vendors || []).filter(v => v && (!v.archived || String(v.id) === String(selectedId || '')))
    .map(v => `<option value="${esc(v.id)}" ${sel(String(selectedId || '') === String(v.id))}>${esc(v.name)}</option>`).join('');
  return `<option value="">— none —</option>${opts}`;
}

/** The assignee picker list: team first (with role), vendors, then names already in use. */
export function rosterDatalistHtml(id: string, people: Array<{ full_name?: string; email?: string; role?: string }>, vendors: WoEditorVendor[], extraNames: string[] = []): string {
  const seen = new Set<string>();
  const opts: string[] = [];
  const add = (name: unknown, hint?: string) => {
    const v = String(name || '').trim();
    if (!v || seen.has(v.toLowerCase())) return;
    seen.add(v.toLowerCase());
    opts.push(`<option value="${esc(v)}"${hint ? ` label="${esc(v)} — ${esc(hint)}"` : ''}></option>`);
  };
  for (const p of people || []) add(p.full_name || p.email, p.role || 'team');
  for (const v of vendors || []) if (!v.archived) add(v.name, 'vendor');
  for (const n of extraNames || []) add(n);
  return `<datalist id="${esc(id)}">${opts.join('')}</datalist>`;
}

/** The four fieldsets (one .mn-wo-groups grid). Markup + attributes identical to the R101 board card. */
export function woEditFormHtml(w: any, ctx: WoEditorCtx): string {
  const id = esc(w.id);
  const gp = w.guest_permission;   // R70 (Mark): guest permission-to-access — mode renders ALWAYS (disabled until granted)
  const curPrio = priorityOf(w);
  const money = ctx.money
    ? `<fieldset class="mn-wo-grp"><legend>Money &amp; billing</legend>
              <label>Cost
                <input type="number" min="0" step="0.01" inputmode="decimal" data-mn-wocost="${id}" value="${w.cost != null ? esc(w.cost) : ''}" placeholder="$" style="width:90px;"/>
              </label>
              <label title="Whose money covers this cost — the field that moves numbers (Costs split, owner queue, owner portal, care report)"><span>Who pays ${infoIcon('wo_payer')}</span>
                <select data-mn-wopayer="${id}">
                  <option value="" ${sel(!w.payer)}>—</option>
                  ${WO_PAYER_OPTS.map(([k, l]) => `<option value="${k}" ${sel(w.payer === k)}>${l}</option>`).join('')}
                </select>
              </label>
              <label title="Michelle's tracker — has the owner been charged for this yet, and how? Changes no totals and never changes who pays"><span>Billed yet? ${infoIcon('wo_billed')}</span>
                <select data-mn-wobilled="${id}">
                  ${WO_BILLED_OPTIONS.map(([k, l]: [string, string]) => `<option value="${k}" ${sel((w.billing_status || '') === k)}>${l}</option>`).join('')}
                </select>
              </label>
              <label title="Low = $45/hr, High = $65/hr — what Michelle bills">Skill
                <select data-mn-woskill="${id}">
                  <option value="" ${sel(!w.skill)}>—</option>
                  <option value="low" ${sel(w.skill === 'low')}>Low ($45)</option>
                  <option value="high" ${sel(w.skill === 'high')}>High ($65)</option>
                </select>
              </label>
              <label class="mn-wo-check" title="Track this work order for an insurance / damage claim">
                <input type="checkbox" data-mn-woclaim="${id}" ${w.claim_tracked ? 'checked' : ''}/> ⚖ Claim tracked
              </label>
              <div class="mn-wo-billhint" data-mn-wobillhint="${id}">${woBillHintHtml(w.payer || '', w.billing_status || '')}</div>
            </fieldset>`
    : `<fieldset class="mn-wo-grp"><legend>Claim</legend>
              <label class="mn-wo-check" title="Track this work order for an insurance / damage claim">
                <input type="checkbox" data-mn-woclaim="${id}" ${w.claim_tracked ? 'checked' : ''}/> ⚖ Claim tracked
              </label>
              <div class="mn-wo-billhint">Cost and billing are kept by an admin or director.</div>
            </fieldset>`;
  return `<div class="mn-wo-groups">
            <fieldset class="mn-wo-grp"><legend>Who &amp; what</legend>
              <label style="flex:1 1 180px;">Assigned to
                <input type="text" list="${esc(ctx.rosterId)}" data-mn-assign="${id}" value="${esc(w.assigned_to)}" placeholder="teammate or vendor — or type a name" autocomplete="off"/>
              </label>
              <label>Route to
                <select data-mn-worole="${id}" title="Which field discipline owns this — cleaner / inspector / maintenance">
                  ${WO_ROLE_OPTS.map(([k, l]) => `<option value="${k}" ${sel((w.role || 'maintenance') === k)}>${l}</option>`).join('')}
                </select>
              </label>
              <label>Vendor
                <select data-mn-wovendor="${id}">
                  ${vendorOptionsHtml(ctx.vendors, w.vendor_id || null)}
                </select>
              </label>
              <label>Priority
                <select data-mn-woprio="${id}">
                  ${WO_PRIORITY_OPTS.map(([k, l]) => `<option value="${k}" ${sel(curPrio === k)}>${l}</option>`).join('')}
                </select>
              </label>
              <label>Status
                <select data-mn-wostatus="${id}">
                  ${WO_STATUS_OPTS.map(([k, l]) => `<option value="${k}" ${sel((w.status || 'open') === k)}>${l}</option>`).join('')}
                </select>
              </label>
            </fieldset>
            <fieldset class="mn-wo-grp"><legend>When</legend>
              <label title="The day this work is BOOKED for — shows on the calendar, the board and the day plan">Booked day
                <input type="date" data-mn-wosched="${id}" value="${esc(schedOf(w) || '')}"/>
              </label>
              <label>Due date
                <input type="date" data-mn-wodue="${id}" value="${esc(w.due_date ? String(w.due_date).slice(0, 10) : '')}" ${w.due_source === 'checkout' ? 'disabled title="Following the next checkout — untick below to set a fixed date"' : ''}/>
              </label>
              <label class="mn-wo-check" title="Due tracks the property's NEXT CHECKOUT automatically — a late checkout or date change moves it with no edits">
                <input type="checkbox" data-mn-woduesrc="${id}" ${w.due_source === 'checkout' ? 'checked' : ''}/> 🔁 Due at next checkout${w.effective_due ? ` <span class="mono" style="color:var(--text-muted);">(${esc(w.effective_due)})</span>` : ''}
              </label>
              <label>Asset
                <select data-mn-woasset="${id}">
                  <option value="">— none —</option>
                  ${(ctx.assets || []).map(a => `<option value="${esc(a.id)}" ${sel(String(w.asset_id || '') === String(a.id))}>${esc(a.name)}</option>`).join('')}
                </select>
              </label>
            </fieldset>
            ${money}
            <fieldset class="mn-wo-grp"><legend>Guest access</legend>
              <label class="mn-wo-check" title="Guest gave permission to enter while in-house">
                <input type="checkbox" data-mn-wogp="${id}" ${gp && gp.granted ? 'checked' : ''}/> 🚪 Guest permission
              </label>
              <label>Access mode
                <select data-mn-wogpmode="${id}" ${gp && gp.granted ? '' : 'disabled'}>
                  <option value="guest_present" ${sel(!gp || gp.mode !== 'own_code')}>guest will be present</option>
                  <option value="own_code" ${sel(!!gp && gp.mode === 'own_code')}>enter with own code</option>
                </select>
              </label>
            </fieldset>
          </div>`;
}

/** Read what the form says for one WO (undefined = that control is not on screen). */
export function readWoForm(scope: ParentNode, id: string): WoEditForm {
  const q = <T extends Element>(attr: string): T | null => scope.querySelector<T>(`[${attr}="${CSS.escape(String(id))}"]`);
  const val = (attr: string): string | undefined => { const el = q<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>(attr); return el ? el.value : undefined; };
  const chk = (attr: string): boolean | undefined => { const el = q<HTMLInputElement>(attr); return el ? !!el.checked : undefined; };
  const modeSel = q<HTMLSelectElement>('data-mn-wogpmode');
  return {
    assigned_to: val('data-mn-assign'),
    role: val('data-mn-worole'),
    vendor_id: val('data-mn-wovendor'),
    priority: val('data-mn-woprio'),
    status: val('data-mn-wostatus'),
    scheduled_date: val('data-mn-wosched'),
    due_date: val('data-mn-wodue'),
    due_checkout: chk('data-mn-woduesrc'),
    asset_id: val('data-mn-woasset'),
    cost: val('data-mn-wocost'),
    payer: val('data-mn-wopayer'),
    billing_status: val('data-mn-wobilled'),
    skill: val('data-mn-woskill'),
    claim_tracked: chk('data-mn-woclaim'),
    gp_granted: chk('data-mn-wogp'),
    gp_mode: modeSel ? modeSel.value : undefined,
    summary: val('data-mn-summary'),
  };
}

/** The POST /work-orders/:id body for what changed on screen, or null. */
export function collectWoEdit(scope: ParentNode, w: any, opts: { money: boolean }): Record<string, any> | null {
  return diffWoEdit(w, readWoForm(scope, String(w.id)), { money: opts.money });
}

/** Live bits inside the fieldsets: the payer/billed explainer and the access-mode enable. Safe to call after every draw. */
export function bindWoEditForm(scope: HTMLElement, id: string, onChange?: () => void): void {
  const q = <T extends Element>(attr: string): T | null => scope.querySelector<T>(`[${attr}="${CSS.escape(String(id))}"]`);
  const payer = q<HTMLSelectElement>('data-mn-wopayer'), billed = q<HTMLSelectElement>('data-mn-wobilled'), hint = q<HTMLElement>('data-mn-wobillhint');
  const reHint = () => { if (hint) hint.innerHTML = woBillHintHtml(payer ? payer.value : '', billed ? billed.value : ''); };
  payer?.addEventListener('change', reHint); billed?.addEventListener('change', reHint);
  const gp = q<HTMLInputElement>('data-mn-wogp'), mode = q<HTMLSelectElement>('data-mn-wogpmode');
  gp?.addEventListener('change', () => { if (mode) mode.disabled = !gp.checked; });
  const due = q<HTMLInputElement>('data-mn-wodue'), dueSrc = q<HTMLInputElement>('data-mn-woduesrc');
  dueSrc?.addEventListener('change', () => { if (due) due.disabled = !!dueSrc.checked; });
  if (onChange) {
    scope.querySelectorAll<HTMLElement>('.mn-wo-groups input, .mn-wo-groups select').forEach(el => {
      el.addEventListener('input', onChange); el.addEventListener('change', onChange);
    });
    const sum = q<HTMLTextAreaElement>('data-mn-summary');
    sum?.addEventListener('input', onChange);
  }
}
